import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Landmark, Ruler } from "lucide-react";
import Seo from "../lib/seo";
import PageHero from "../components/PageHero";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { SectionHead } from "../components/ui-bits";
import { getState } from "../data/states";
import { SERVICES } from "../data/services";
import Faq from "../components/Faq";

let countiesCache = null;
export function useCounties(stateSlug) {
  const [counties, setCounties] = useState(countiesCache?.[stateSlug] || null);
  useEffect(() => {
    let live = true;
    if (countiesCache) {
      setCounties(countiesCache[stateSlug] || []);
      return undefined;
    }
    fetch("/data/counties.json")
      .then((r) => r.json())
      .then((d) => {
        countiesCache = d;
        if (live) setCounties(d[stateSlug] || []);
      })
      .catch(() => live && setCounties([]));
    return () => {
      live = false;
    };
  }, [stateSlug]);
  return counties;
}

export default function CountyPage() {
  const { slug, county } = useParams();
  const state = getState(slug);
  const counties = useCounties(slug);

  if (!state) return <Navigate to="/404" replace />;
  if (counties === null) return null;
  const info = counties.find((c) => c.s === county);
  if (!info) return <Navigate to="/404" replace />;

  const nearby = counties.filter((c) => c.s !== county).slice(0, 14);

  return (
    <>
      <Seo
        title={`Land Survey CAD Drafting in ${info.n}, ${state.name} | GeoDrafts`}
        description={`Remote land survey CAD drafting for surveying firms in ${info.n}, ${state.name} — boundary, topographic, ALTA/NSPS, plat and utility drafting to county formats as you direct.`}
        path={`/service-areas/${state.slug}/county/${info.s}`}
        geo={{ region: `US-${state.abbr}`, placename: `${info.n}, ${state.name}` }}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://geodrafts.com/" },
                { "@type": "ListItem", position: 2, name: "Service Areas", item: "https://geodrafts.com/service-areas" },
                { "@type": "ListItem", position: 3, name: state.name, item: `https://geodrafts.com/service-areas/${state.slug}` },
                { "@type": "ListItem", position: 4, name: info.n, item: `https://geodrafts.com/service-areas/${state.slug}/county/${info.s}` },
              ],
            },
            {
              "@type": "Service",
              name: `Land Survey CAD Drafting — ${info.n}, ${state.abbr}`,
              provider: { "@type": "Organization", name: "GeoDrafts", url: "https://geodrafts.com" },
              areaServed: `${info.n}, ${state.name}`,
              serviceType: "Land Surveying CAD Drafting and GIS Services",
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: `Do you draft to ${info.n} recording standards?`,
                  acceptedAnswer: { "@type": "Answer", text: `Yes — share the ${info.n} recording requirements or an approved example sheet and we draft to them exactly as directed. Certification always remains with your licensed ${state.name} surveyor.` },
                },
                {
                  "@type": "Question",
                  name: `How fast is drafting production for ${info.n} projects?`,
                  acceptedAnswer: { "@type": "Answer", text: "Most projects are scoped within one business day and drafted on the agreed schedule — send data in the evening, review sheets the next morning." },
                },
              ],
            },
          ],
        }}
      />

      <PageHero
        testid={`county-hero-${info.s}`}
        eyebrow={`Service Area — ${state.abbr}`}
        title={`Survey CAD Drafting in ${info.n}`}
        lead={`GeoDrafts supports licensed surveying firms working in ${info.n}, ${state.name} with remote drafting production — plats, plans and maps prepared to the recording formats your office directs.`}
        crumbs={[
          { label: "Home", to: "/" },
          { label: "Service Areas", to: "/service-areas" },
          { label: state.name, to: `/service-areas/${state.slug}` },
          { label: info.n },
        ]}
        aside={{
          kicker: `${info.n}, ${state.abbr}`,
          line: `Local formats. Statewide standards. Overnight drafting.`,
          points: [
            `${state.land} conventions followed as instructed`,
            `${state.name} licensing context: ${state.board.split("—")[0].trim()}`,
            "County recording sheet formats per your direction",
            "All 22 drafting services available",
          ],
          foot: "Remote production · Your certification",
        }}
      />

      {/* County context */}
      <section className="bg-white py-16 lg:py-20" data-testid="county-context">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-6">
          {[
            { icon: MapPin, label: "Coverage Area", value: `${info.n}, ${state.name}` },
            { icon: Ruler, label: "Approx. Land Area", value: info.a ? `${info.a.toLocaleString()} sq mi` : `${state.name} jurisdiction` },
            { icon: Landmark, label: "Survey Standards Context", value: state.board },
          ].map((c) => (
            <Reveal key={c.label}>
              <div className="h-full bg-paleslate border border-lineblue rounded-md p-6">
                <c.icon className="h-5 w-5 text-aqua" strokeWidth={1.75} />
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-mutedslate">{c.label}</p>
                <p className="mt-1.5 text-sm font-bold text-navy leading-snug">{c.value}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services available in this county */}
      <section className="bg-paleslate py-20 lg:py-28" data-testid="county-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Service Matrix"
            title={`Drafting Services in ${info.n}`}
            lead={`Every service produced remotely for ${info.n} surveyors — your data, your templates, your professional review.`}
          />
          <Stagger className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => (
              <StaggerItem key={s.slug}>
                <Link
                  to={`/${s.slug}`}
                  data-testid={`county-service-${s.slug}`}
                  className="group flex items-center justify-between gap-3 bg-white border border-lineblue rounded-md px-5 py-4 hover:border-aqua/50 hover:shadow-[0_8px_24px_rgba(16,42,67,0.08)] transition-all"
                >
                  <span className="font-display font-bold text-navy text-sm leading-snug group-hover:text-steel transition-colors">
                    {s.name}
                  </span>
                  <ArrowRight className="h-4 w-4 text-steel group-hover:text-aqua group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* 10 core keyword clusters */}
      <section className="bg-white py-20 lg:py-28" data-testid="county-clusters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="How We Help Here"
            title={`Specialist Drafting in ${info.n}`}
            lead="The ten production areas local surveyors request most — each with its own dedicated service page."
          />
          <Stagger className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ["Outsourced Survey CAD Drafting", "/survey-cad-outsourcing"],
              ["CAD Drafting for Land Surveyors", "/land-survey-cad-drafting"],
              ["Land Survey Plan Drafting", "/land-survey-cad-drafting"],
              ["Topographic Survey Drafting", "/topographic-survey-drafting"],
              ["Boundary & Property Survey Drafting", "/boundary-survey-drafting"],
              ["Civil 3D Survey Drafting", "/civil-3d-survey-drafting"],
              ["GIS Mapping & Data Conversion", "/gis-mapping-services"],
              ["Survey Data & PDF to DWG Conversion", "/survey-data-conversion"],
              ["Point Cloud / LiDAR to CAD", "/lidar-point-cloud-to-cad"],
              ["As-Built & Existing Conditions CAD", "/as-built-survey-drafting"],
            ].map(([label, to]) => (
              <StaggerItem key={label}>
                <Link
                  to={to}
                  data-testid={`county-cluster-${to.slice(1)}`}
                  className="group flex items-center justify-between gap-3 bg-paleslate border border-lineblue rounded-md px-5 py-4 hover:bg-white hover:border-aqua/50 transition-all"
                >
                  <span className="font-display font-bold text-navy text-sm leading-snug group-hover:text-steel transition-colors">
                    {label} in {info.n}
                  </span>
                  <ArrowRight className="h-4 w-4 text-steel group-hover:text-aqua group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              </StaggerItem>
            ))}
          </Stagger>

          <div className="mt-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedslate mb-3">How {info.n} firms search for us</p>
            <div className="flex flex-wrap gap-1.5">
              {[
                `land survey CAD drafting services provider ${info.n}`,
                `survey drafting company ${info.n} ${state.abbr}`,
                `outsourced CAD drafting ${info.n}`,
                `topographic survey drawings ${info.n}`,
                `boundary survey drafting ${state.name}`,
                `CAD production support for surveyors ${state.abbr}`,
              ].map((k) => (
                <span key={k} className="font-mono text-[10px] tracking-wider text-mutedslate bg-paleslate border border-lineblue rounded-sm px-2.5 py-1.5" data-testid="county-search-chip">
                  {k}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-paleslate py-20 lg:py-24 border-t border-lineblue" data-testid="county-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="FAQ" title={`${info.n} Drafting — Common Questions`} align="center" />
          <div className="mt-10">
            <Faq
              testPrefix="county-faq"
              items={[
                {
                  q: `Do you draft to ${info.n} recording standards?`,
                  a: `Yes — share the ${info.n} recording requirements or an approved example sheet and we draft to them exactly as directed. Certification always remains with your licensed ${state.name} surveyor.`,
                },
                {
                  q: `How fast is drafting production for ${info.n} projects?`,
                  a: "Most projects are scoped within one business day and drafted on the agreed schedule — send data in the evening, review sheets the next morning.",
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Nearby counties */}
      <section className="bg-white py-14 border-t border-lineblue" data-testid="county-nearby">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedslate mb-4">
            More coverage in {state.name} — {counties.length} {state.abbr === "LA" ? "parishes" : state.abbr === "AK" ? "boroughs & areas" : "counties"}
          </p>
          <div className="flex flex-wrap gap-2">
            {nearby.map((c) => (
              <Link
                key={c.s}
                to={`/service-areas/${state.slug}/county/${c.s}`}
                data-testid={`county-nearby-${c.s}`}
                className="font-mono text-[10px] tracking-wider text-steel border border-lineblue rounded-sm px-3 py-1.5 hover:border-aqua hover:text-aqua transition-colors"
              >
                {c.n}
              </Link>
            ))}
            <Link to={`/service-areas/${state.slug}`} data-testid="county-back-state" className="font-mono text-[10px] tracking-wider text-aqua border border-aqua/40 rounded-sm px-3 py-1.5 hover:bg-aqua hover:text-navy transition-colors">
              All {state.name} →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-deep py-16 lg:py-20 relative overflow-hidden" data-testid="county-cta">
        <div className="absolute inset-0 bg-contours-dark" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <h2 className="text-3xl font-bold text-white">Working in {info.n}?</h2>
            <p className="mt-3 text-white/70 max-w-xl">
              Send your field data and the county format you record to — receive a scoped proposal with a confirmed delivery schedule.
            </p>
          </div>
          <Link
            to="/request-a-quote"
            data-testid="county-cta-quote"
            className="shrink-0 inline-flex items-center gap-2 bg-aqua text-navy font-bold text-sm px-7 py-3.5 rounded-md hover:bg-aqua-deep transition-all"
          >
            Request a Quote <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
